import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ClaimRejectionNotice } from './ClaimRejectionNotice';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Code, Eye, Copy, Check, Download, RefreshCw, X, 
  FileText, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { CMS1500FormPreview } from './CMS1500FormPreview';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Universal Claim Viewer — use from anywhere.
 * Shows three tabs:
 *   1. CMS-1500 (custom HTML view)
 *   2. CMS-1500 Form (filled NUCC PDF — print/mailable)
 *   3. ANSI X12 raw envelope
 *
 * Props:
 *   claimId: string — the claim ID to preview (fetches from backend)
 *   noteId?: string — optional: for the Prepare-a-Claim flow before
 *           the claim has been created. When provided, the
 *           "CMS-1500 Form" tab fetches via /preview-from-note.
 *   open: boolean — controls dialog visibility
 *   onClose: () => void — called when dialog closes
 *   clientName?: string — optional display name
 *   previewData?: { parsed, content, is_preview } — optional pre-fetched data (skips API call)
 */
export const ClaimViewer = ({ claimId, noteId, open, onClose, clientName, previewData, onClaimRebuilt }) => {
  // Lightweight claim-doc fetch — powers the unified rejection banner
  // (277CA / CSR reasons) at the top of the viewer.
  const [claimDoc, setClaimDoc] = useState(null);
  useEffect(() => {
    if (!open || !claimId) { setClaimDoc(null); return; }
    let cancelled = false;
    axios.get(`${API}/api/claims/${claimId}`)
      .then((r) => { if (!cancelled) setClaimDoc(r.data?.claim || null); })
      .catch(() => { if (!cancelled) setClaimDoc(null); });
    return () => { cancelled = true; };
  }, [open, claimId]);
  // Default to the PDF tab. The legacy HTML "in-house" CMS-1500 view
  // is hidden by default — it duplicated the PDF mapper and drifted
  // out of parity (e.g., on payers with individual billing it could
  // show a different NPI in Box 24J than the PDF). The PDF is now
  // the single source of truth. Set the URL hash to `#html-1500` to
  // re-expose the legacy tab for one-off troubleshooting.
  const showLegacyHtmlTab = typeof window !== 'undefined' && window.location.hash === '#html-1500';
  const [tab, setTab] = useState(showLegacyHtmlTab ? '1500' : 'pdf');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [rawAnsi, setRawAnsi] = useState('');
  // EDI segments grouped by X12 loop with per-payer override badges —
  // see backend `services/edi_loop_grouper.group_837p_by_loop`.  Populated
  // alongside `rawAnsi` from the same preview endpoint, used to render
  // the "Loops" tab.
  const [ediLoops, setEdiLoops] = useState([]);
  const [copied, setCopied] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  // Submitted ANSI snapshot — the exact ST→SE block that was wired
  // out at the time the parent batch was closed/submitted.  Used by
  // the "Submitted ANSI" tab so the biller can compare a fresh
  // preview against what was actually transmitted.  Feb 2026 Phase A.
  const [submittedSnapshot, setSubmittedSnapshot] = useState(null);
  const [submittedSnapshotLoading, setSubmittedSnapshotLoading] = useState(false);
  const [submittedSnapshotError, setSubmittedSnapshotError] = useState(null);
  const [showSnapshotCompare, setShowSnapshotCompare] = useState(false);
  const [warnings, setWarnings] = useState([]);

  const fetchPreview = useCallback(async () => {
    if (!claimId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/api/claims/${claimId}/preview-837p`);
      setParsed(response.data.parsed);
      setRawAnsi(response.data.content);
      setEdiLoops(response.data.edi_loops || []);
      setWarnings(response.data.warnings || []);
      setIsPreview(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load claim preview');
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  // Fetch the per-claim ANSI snapshot stamped at batch close /
  // submission.  Returns null when the claim hasn't been batched yet
  // OR predates the snapshot feature.  Feb 2026 Phase A — paired with
  // the "Submitted ANSI" tab to let billers compare a fresh preview
  // against the version that actually got transmitted.
  const fetchSubmittedSnapshot = useCallback(async () => {
    if (!claimId) return;
    setSubmittedSnapshotLoading(true);
    setSubmittedSnapshotError(null);
    try {
      const resp = await axios.get(
        `${API}/api/billing/claims/${claimId}/submitted-837p`,
      );
      setSubmittedSnapshot(resp.data || null);
    } catch (err) {
      setSubmittedSnapshotError(
        err.response?.data?.detail || 'Failed to load submitted ANSI snapshot',
      );
      setSubmittedSnapshot(null);
    } finally {
      setSubmittedSnapshotLoading(false);
    }
  }, [claimId]);

  useEffect(() => {
    if (open && previewData) {
      setParsed(previewData.parsed);
      setRawAnsi(previewData.content || '');
      setEdiLoops(previewData.edi_loops || []);
      setWarnings(previewData.warnings || []);
      setIsPreview(!!previewData.is_preview);
      setError(null);
      setLoading(false);
      setTab('1500');
    } else if (open && claimId) {
      fetchPreview();
      setTab('1500');
    }
  }, [open, claimId, previewData, fetchPreview]);

  // Lazy-load the submitted snapshot only when the user clicks the
  // "Submitted ANSI" tab (avoids an extra HTTP call on every viewer
  // open; the snapshot endpoint is cheap but unused for most users).
  useEffect(() => {
    if (open && claimId && tab === 'submitted' && submittedSnapshot === null && !submittedSnapshotLoading && !submittedSnapshotError) {
      fetchSubmittedSnapshot();
    }
  }, [open, claimId, tab, submittedSnapshot, submittedSnapshotLoading, submittedSnapshotError, fetchSubmittedSnapshot]);

  // Reset snapshot state when the claim changes / dialog closes so a
  // stale snapshot from a prior claim never flashes when the user
  // opens a different one.
  useEffect(() => {
    if (!open) {
      setSubmittedSnapshot(null);
      setSubmittedSnapshotError(null);
      setShowSnapshotCompare(false);
    }
  }, [open, claimId]);

  const handleCopy = () => {
    navigator.clipboard.writeText(rawAnsi);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('837P copied to clipboard');
  };

  const handleDownload = () => {
    const blob = new Blob([rawAnsi], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `837P_${claimId || 'claim'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const displayName = clientName || parsed?.subscriber?.name || 'Claim';

  // Rebuild eligibility — mirrors ClaimActionsMenu / backend gate.
  // Pure self-pay claims (no payer, no insurance payment posted) may
  // rebuild as "Convert to Insurance & Rebuild"; other self_pay/
  // client_owes claims stay blocked.
  const vClaim = parsed?.claim || {};
  const vStatus = String(vClaim.status || '').toLowerCase();
  const vInsName = String(vClaim.primary_insurance || '').trim().toLowerCase().replace('-', ' ');
  const vPureSelfPay = (!!vClaim.is_self_pay || vInsName === 'self pay' || !vClaim.primary_insurance)
    && !(Number(vClaim.primary_paid_amount) > 0)
    && !vClaim.era_id;
  const vSelfPayStatus = ['self_pay', 'client_owes'].includes(vStatus);
  const vSelfPayConvert = vSelfPayStatus && vPureSelfPay;
  const canRebuildFromViewer = !!claimId && !isPreview && !!vClaim.status
    && !['paid', 'voided', 'superseded'].includes(vStatus)
    && (!vSelfPayStatus || vPureSelfPay)
    && !vClaim.is_superseded;

  // Detect which clearinghouse envelope this preview reflects, from the
  // NM1*40 (Receiver Name) segment in the raw 837P. Lets billers see at
  // a glance whether the file would route to NEHEN or Availity for this
  // claim's primary payer.
  const clearinghouseMatch = rawAnsi.match(/NM1\*40\*2\*([A-Z]+)/i);
  const detectedClearinghouse = clearinghouseMatch ? clearinghouseMatch[1].toUpperCase() : null;
  const clearinghouseBadgeColor = detectedClearinghouse === 'NEHEN'
    ? 'bg-violet-600'
    : detectedClearinghouse === 'AVAILITY'
      ? 'bg-blue-600'
      : 'bg-gray-500';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-7xl max-h-[92vh] w-[95vw] flex flex-col p-0 gap-0" data-testid="claim-viewer-dialog">
        {/* a11y: Radix requires a DialogTitle inside DialogContent */}
        <DialogTitle className="sr-only">Claim Preview</DialogTitle>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-white rounded-t-lg">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-indigo-600" />
            <span className="font-semibold text-gray-900">Claim Preview</span>
            <span className="text-gray-500">—</span>
            <span className="text-gray-700">{displayName}</span>
            {parsed?.claim?.claim_number && (
              <Badge variant="outline" className="text-xs font-mono">{parsed.claim.claim_number}</Badge>
            )}
            {detectedClearinghouse && (
              <Badge
                className={`${clearinghouseBadgeColor} text-white text-xs uppercase tracking-wide`}
                data-testid="claim-clearinghouse-badge"
                title={`837P routed via ${detectedClearinghouse} based on this payer's clearinghouse setting`}
              >
                {detectedClearinghouse}
              </Badge>
            )}
            {parsed?.billing_provider?.entity_type && (
              <Badge
                variant="outline"
                className="text-xs font-medium border-purple-300 text-purple-700 bg-purple-50"
                data-testid="claim-billing-entity-badge"
                title={`Loop 2010AA NM1*85 — ${parsed.billing_provider.entity_type} billing\n${parsed.billing_provider.name || ''}\nNPI: ${parsed.billing_provider.npi || '—'}`}
              >
                Billing as: {String(parsed.billing_provider.entity_type).charAt(0).toUpperCase() + String(parsed.billing_provider.entity_type).slice(1)}
                {parsed.billing_provider.name ? ` — ${parsed.billing_provider.name}` : ''}
              </Badge>
            )}
            {isPreview && (
              <Badge className="bg-amber-500 text-white text-xs">DRAFT PREVIEW</Badge>
            )}
          </div>
        </div>

        {/* COB / payer-profile warnings surfaced by the preview endpoint
            (e.g. missing carrier code for cob_use_carrier_code payers) */}
        {warnings.length > 0 && (
          <div
            className="mx-5 mt-2 px-3 py-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-xs space-y-1"
            data-testid="claim-preview-warnings"
          >
            {warnings.map((w, i) => (
              <div key={i} className="flex gap-1.5">
                <span className="font-semibold shrink-0">Warning:</span>
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}

        {/* Unified rejection banner — same treatment as the workshop and
            batch dialog (277CA + CSR clearinghouse/payer rejections). */}
        {claimDoc && (
          <div className="mx-5 mt-2">
            <ClaimRejectionNotice claim={claimDoc} variant="banner" />
          </div>
        )}

        {/* Tab bar + actions */}
        <div className="flex items-center justify-between px-5 py-2 border-b bg-gray-50">
          <div className="flex gap-1 bg-white rounded-lg p-0.5 border shadow-sm">
            {showLegacyHtmlTab && (
              <button
                onClick={() => setTab('1500')}
                className={`px-4 py-1.5 text-sm rounded-md transition-all ${tab === '1500' ? 'bg-indigo-600 text-white font-medium shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                data-testid="claim-tab-1500"
                title="Legacy in-house HTML view — superseded by the PDF tab. Visible only when URL has #html-1500."
              >
                <Eye className="h-3.5 w-3.5 inline mr-1.5" />CMS-1500 (legacy)
              </button>
            )}
            <button
              onClick={() => setTab('pdf')}
              className={`px-4 py-1.5 text-sm rounded-md transition-all ${tab === 'pdf' ? 'bg-indigo-600 text-white font-medium shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="claim-tab-pdf"
              title="Filled CMS-1500 (02-12) PDF — for printing/mailing"
            >
              <FileText className="h-3.5 w-3.5 inline mr-1.5" />CMS-1500
            </button>
            <button
              onClick={() => setTab('ansi')}
              className={`px-4 py-1.5 text-sm rounded-md transition-all ${tab === 'ansi' ? 'bg-indigo-600 text-white font-medium shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="claim-tab-ansi"
            >
              <Code className="h-3.5 w-3.5 inline mr-1.5" />ANSI X12
            </button>
            <button
              onClick={() => setTab('loops')}
              className={`px-4 py-1.5 text-sm rounded-md transition-all ${tab === 'loops' ? 'bg-indigo-600 text-white font-medium shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="claim-tab-loops"
              title="ANSI X12 grouped by loop (2010AA, 2310B, etc.) with per-payer override highlights"
            >
              <Code className="h-3.5 w-3.5 inline mr-1.5" />Loops
            </button>
            <button
              onClick={() => setTab('submitted')}
              className={`px-4 py-1.5 text-sm rounded-md transition-all ${tab === 'submitted' ? 'bg-indigo-600 text-white font-medium shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="claim-tab-submitted"
              title="Exact ST→SE block transmitted at batch close / submission — compare against the current preview to see what changed"
            >
              <FileText className="h-3.5 w-3.5 inline mr-1.5" />Submitted ANSI
            </button>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleCopy} disabled={!rawAnsi} data-testid="claim-copy-btn">
              {copied ? <Check className="h-3.5 w-3.5 mr-1 text-green-600" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleDownload} disabled={!rawAnsi} data-testid="claim-download-btn">
              <Download className="h-3.5 w-3.5 mr-1" />
              Download
            </Button>
            {/* Rebuild from Chart — same backend endpoint
                ClaimEditDialog uses.  Pre-submission claims get
                refreshed in place; submitted claims spawn a v2. */}
            {canRebuildFromViewer && (
              <Button
                size="sm"
                variant="outline"
                className="border-teal-600 text-teal-700 hover:bg-teal-50"
                data-testid="claim-viewer-rebuild-btn"
                onClick={async () => {
                  const reason = window.prompt(
                    vSelfPayConvert
                      ? `Convert claim ${parsed?.claim?.claim_number || ''} to insurance and rebuild?\n\n`
                        + 'Re-reads the current chart (insurance, diagnoses, CPT, POS, DOS) '
                        + 'and rebuilds this self-pay claim as an insurance claim.\n\n'
                        + '• Status moves to Ready for Billing\n'
                        + '• Any amount the client already paid moves to their escrow for reapplication\n\n'
                        + 'Reason (recommended):'
                      : `Rebuild claim ${parsed?.claim?.claim_number || ''} from the chart?\n\n`
                        + 'Re-reads the current chart (insurance, diagnoses, CPT, POS, DOS) '
                        + 'and updates this claim.\n\n'
                        + '• Pre-submission claims update in place (no new version)\n'
                        + '• Already-submitted claims spawn a v2 (old 837 stays addressable)\n\n'
                        + 'Reason (recommended):',
                    vSelfPayConvert
                      ? 'Client now has insurance — converting self-pay claim'
                      : 'Chart corrected — refreshing claim data',
                  );
                  if (reason === null) return;
                  try {
                    const r = await axios.post(
                      `${API}/api/claims/${claimId}/rebuild-from-chart`,
                      { reason: reason || 'Rebuild from chart' },
                    );
                    toast.success(r.data?.message || 'Claim refreshed.');
                    if (typeof onClaimRebuilt === 'function') {
                      onClaimRebuilt(r.data);
                    }
                    // Reload the viewer so the user sees the fresh data
                    await fetchPreview();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Rebuild failed');
                  }
                }}
                title={vSelfPayConvert
                  ? "Rebuild this self-pay claim as an insurance claim from the current chart"
                  : "Re-read the chart and refresh this claim's insurance, diagnoses, CPT, POS, and DOS"}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                {vSelfPayConvert ? 'Convert to Insurance & Rebuild' : 'Rebuild from Chart'}
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Loading claim data...
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <AlertCircle className="h-8 w-8 text-red-400 mb-3" />
              <p className="text-red-600 font-medium">{error}</p>
            </div>
          ) : tab === '1500' && parsed ? (
            <CMS1500View parsed={parsed} />
          ) : tab === 'pdf' ? (
            <div className="p-4">
              {claimId ? (
                <CMS1500FormPreview mode="claim" claimId={claimId} />
              ) : noteId ? (
                <CMS1500FormPreview mode="note-preview" noteId={noteId} />
              ) : (
                <div className="text-sm text-gray-500 p-4">
                  Save the claim to view the filled CMS-1500 PDF.
                </div>
              )}
            </div>
          ) : tab === 'ansi' ? (
            <div className="bg-gray-950 min-h-[400px] p-5">
              <pre className="text-green-400 text-xs font-mono whitespace-pre-wrap break-all leading-relaxed" data-testid="ansi-raw-content">
                {rawAnsi || 'No ANSI content generated.'}
              </pre>
            </div>
          ) : tab === 'loops' ? (
            <LoopsView loops={ediLoops} />
          ) : tab === 'submitted' ? (
            <SubmittedAnsiTab
              loading={submittedSnapshotLoading}
              error={submittedSnapshotError}
              snapshotData={submittedSnapshot}
              currentPreview={rawAnsi}
              showCompare={showSnapshotCompare}
              onToggleCompare={() => setShowSnapshotCompare((v) => !v)}
              onRefresh={fetchSubmittedSnapshot}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** CMS-1500 form-style view — ALL 33 boxes, mirrors the official red-ink paper form */
const CMS1500View = ({ parsed }) => {
  const p = parsed;
  const R = '#c00000';
  const LR = '#fef2f2';
  const B = '#999';

  /**
   * CMS-1500 dates use MM DD YY (two-digit month, day, year — space-separated, no slashes).
   * Backend stores ISO YYYY-MM-DD; convert defensively. Falls back to the raw value
   * if it doesn't match the expected ISO shape (so we never accidentally hide data).
   */
  const fmtDos = (raw) => {
    if (!raw) return '';
    const s = String(raw);
    const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, yyyy, mm, dd] = isoMatch;
      return `${mm} ${dd} ${yyyy.slice(-2)}`;
    }
    return s;
  };

  const F = ({ n, label, children, className = '' }) => (
    <div className={`relative ${className}`} style={{ borderColor: B }}>
      <div className="px-1 pt-[1px] leading-none">
        <span className="uppercase tracking-wide font-semibold" style={{ fontSize: '6.5px', color: R, letterSpacing: '0.03em' }}>
          {n ? `${n}. ` : ''}{label}
        </span>
      </div>
      <div className="px-1.5 pb-0.5 min-h-[18px]">{children}</div>
    </div>
  );

  const V = ({ children, mono }) => (
    <span className={`text-[10px] text-gray-900 leading-tight ${mono ? 'font-mono' : ''}`}>{children || '\u00A0'}</span>
  );

  const Chk = ({ checked, label }) => (
    <span className="inline-flex items-center gap-[2px] mr-1">
      <span
        className="inline-flex items-center justify-center w-[10px] h-[10px] border leading-none"
        style={{ borderColor: B, background: '#fff' }}
      >
        {checked && (
          <span
            style={{ fontSize: '10px', color: '#000', fontFamily: 'monospace', fontWeight: 700, lineHeight: 1, marginTop: '-2px' }}
            data-testid={`chk-${label}-on`}
          >
            ✕
          </span>
        )}
      </span>
      <span style={{ fontSize: '6.5px', color: '#555' }}>{label}</span>
    </span>
  );

  /* Cell wrapper — applies border consistently */
  const C = ({ children, className = '', colSpan }) => (
    <div className={`border-b border-r ${className}`} style={{ borderColor: B, gridColumn: colSpan ? `span ${colSpan}` : undefined }}>
      {children}
    </div>
  );

  return (
    <div className="p-3 bg-gray-200" data-testid="cms-1500-view">
      <div className="max-w-[820px] mx-auto shadow-xl" style={{ fontFamily: "'Courier New', Courier, monospace" }}>

        {/* CARRIER BLOCK */}
        <div className="bg-white px-6 py-2 text-center border border-b-0" style={{ borderColor: B }}>
          <div style={{ fontSize: '7px', color: '#888', letterSpacing: '0.05em' }} className="uppercase mb-0.5">CARRIER</div>
          <div className="text-sm font-bold text-gray-900 tracking-wide" data-testid="carrier-name">
            {p.insurance?.name || 'INSURANCE COMPANY'}
          </div>
          {p.insurance?.payer_id && <div className="text-[9px] text-gray-500 font-mono">Payer ID: {p.insurance.payer_id}</div>}
        </div>

        {/* FORM CONTAINER */}
        <div className="bg-white border-2" style={{ borderColor: R }}>

          {/* TITLE BAR */}
          <div className="flex items-center justify-between px-3 py-[5px]" style={{ background: R }}>
            <div className="flex items-center gap-2">
              <span className="text-white font-bold tracking-[0.12em]" style={{ fontSize: '10px' }}>HEALTH INSURANCE CLAIM FORM</span>
              <span className="text-[7px] tracking-wider" style={{ color: '#ffb3b3' }}>APPROVED BY NATIONAL UNIFORM CLAIM COMMITTEE (NUCC) 02/12</span>
            </div>
            <span className="text-[9px] font-bold tracking-widest" style={{ color: '#ffcccc' }}>CMS-1500</span>
          </div>

          {/* ═══ ROW: BOX 1 + 1a ═══ */}
          <div className="flex border-b" style={{ borderColor: B }}>
            <div className="flex-1 px-1.5 py-[2px] flex items-center flex-wrap gap-x-0.5" style={{ background: LR }}>
              <span className="font-bold mr-0.5" style={{ fontSize: '6.5px', color: R }}>1.</span>
              {['MEDICARE','MEDICAID','TRICARE','CHAMPVA','GRP HEALTH PLAN','FECA BLK LUNG','OTHER'].map(t => (
                <Chk key={t} label={t} checked={t === 'OTHER'} />
              ))}
            </div>
            <C className="min-w-[200px] !border-b-0">
              <F n="1a" label="INSURED'S I.D. NUMBER"><V mono>{p.insurance?.member_id}</V></F>
            </C>
          </div>

          {/* ═══ 3-COL GRID: Patient (left) | Middle | Insured (right) ═══ */}
          {/* Row: 2 | 3 | 4 */}
          <div className="grid grid-cols-3" style={{ borderColor: B }}>
            <C><F n="2" label="PATIENT'S NAME (Last, First, MI)"><V>{p.subscriber?.name}</V></F></C>
            <C>
              <F n="3" label="PATIENT'S BIRTH DATE &nbsp;&nbsp;&nbsp; SEX">
                <div className="flex items-center gap-1">
                  <V mono>{p.subscriber?.dob}</V>
                  <Chk label="M" checked={p.subscriber?.sex === 'M'} />
                  <Chk label="F" checked={p.subscriber?.sex === 'F'} />
                </div>
              </F>
            </C>
            <C className="!border-r-0"><F n="4" label="INSURED'S NAME (Last, First, MI)"><V>{p.subscriber?.name}</V></F></C>
          </div>

          {/* Row: 5 | 6 | 7 */}
          <div className="grid grid-cols-3" style={{ borderColor: B }}>
            <C>
              <F n="5" label="PATIENT'S ADDRESS (No., Street)"><V>{p.subscriber?.address}</V></F>
            </C>
            <C>
              <F n="6" label="PATIENT RELATIONSHIP TO INSURED">
                <div className="flex gap-1">
                  <Chk label="Self" checked /><Chk label="Spouse" /><Chk label="Child" /><Chk label="Other" />
                </div>
              </F>
            </C>
            <C className="!border-r-0">
              <F n="7" label="INSURED'S ADDRESS (No., Street)"><V>{p.subscriber?.address}</V></F>
            </C>
          </div>

          {/* Row: 5-city/state/zip | 8 | 7-city/state/zip */}
          <div className="grid grid-cols-3" style={{ borderColor: B }}>
            <C>
              <div className="grid grid-cols-3">
                <div className="border-r px-1 py-[1px]" style={{ borderColor: B }}>
                  <span style={{ fontSize: '6px', color: R }} className="uppercase font-semibold">CITY</span>
                  <div><V>{p.subscriber?.city}</V></div>
                </div>
                <div className="border-r px-1 py-[1px]" style={{ borderColor: B }}>
                  <span style={{ fontSize: '6px', color: R }} className="uppercase font-semibold">STATE</span>
                  <div><V>{p.subscriber?.state}</V></div>
                </div>
                <div className="px-1 py-[1px]">
                  <span style={{ fontSize: '6px', color: R }} className="uppercase font-semibold">ZIP CODE</span>
                  <div><V mono>{p.subscriber?.zip}</V></div>
                </div>
              </div>
            </C>
            <C><F n="8" label="RESERVED FOR NUCC USE"><V>{'\u00A0'}</V></F></C>
            <C className="!border-r-0">
              <div className="grid grid-cols-3">
                <div className="border-r px-1 py-[1px]" style={{ borderColor: B }}>
                  <span style={{ fontSize: '6px', color: R }} className="uppercase font-semibold">CITY</span>
                  <div><V>{p.subscriber?.city}</V></div>
                </div>
                <div className="border-r px-1 py-[1px]" style={{ borderColor: B }}>
                  <span style={{ fontSize: '6px', color: R }} className="uppercase font-semibold">STATE</span>
                  <div><V>{p.subscriber?.state}</V></div>
                </div>
                <div className="px-1 py-[1px]">
                  <span style={{ fontSize: '6px', color: R }} className="uppercase font-semibold">ZIP CODE</span>
                  <div><V mono>{p.subscriber?.zip}</V></div>
                </div>
              </div>
            </C>
          </div>

          {/* Row: 5-phone | (empty) | 7-phone */}
          <div className="grid grid-cols-3" style={{ borderColor: B }}>
            <C>
              <div className="px-1 py-[1px]">
                <span style={{ fontSize: '6px', color: R }} className="uppercase font-semibold">TELEPHONE (Include Area Code)</span>
                <div><V mono>{p.submitter?.phone}</V></div>
              </div>
            </C>
            <C><div className="min-h-[18px]" /></C>
            <C className="!border-r-0">
              <div className="px-1 py-[1px]">
                <span style={{ fontSize: '6px', color: R }} className="uppercase font-semibold">TELEPHONE (Include Area Code)</span>
                <div><V>{'\u00A0'}</V></div>
              </div>
            </C>
          </div>

          {/* Row: 9 | 10 | 11 */}
          <div className="grid grid-cols-3" style={{ borderColor: B }}>
            <C><F n="9" label="OTHER INSURED'S NAME (Last, First, MI)"><V mono>{p.secondary_insurance?.holder_name ? (() => { const h = String(p.secondary_insurance.holder_name).trim(); if (h.includes(',')) return h.toUpperCase(); const parts = h.split(/\s+/); return parts.length >= 2 ? `${parts[parts.length - 1]}, ${parts.slice(0, -1).join(' ')}`.toUpperCase() : h.toUpperCase(); })() : '\u00A0'}</V></F></C>
            <C>
              <F n="10" label="IS PATIENT'S CONDITION RELATED TO:"><V>{'\u00A0'}</V></F>
            </C>
            <C className="!border-r-0"><F n="11" label="INSURED'S POLICY GROUP OR FECA NUMBER"><V mono>{p.insurance?.group_number}</V></F></C>
          </div>

          {/* Row: 9a | 10a | 11a */}
          <div className="grid grid-cols-3" style={{ borderColor: B }}>
            <C><F n="9a" label="OTHER INSURED'S POLICY OR GROUP NUMBER"><V mono>{p.secondary_insurance?.group_number || p.secondary_insurance?.member_id || '\u00A0'}</V></F></C>
            <C>
              <F n="" label="a. EMPLOYMENT? (Current or Previous)">
                <div className="flex gap-1"><Chk label="YES" /><Chk label="NO" checked /></div>
              </F>
            </C>
            <C className="!border-r-0">
              <F n="11a" label="INSURED'S DATE OF BIRTH &nbsp;&nbsp; SEX">
                <div className="flex items-center gap-1">
                  <V mono>{p.subscriber?.dob}</V>
                  <Chk label="M" checked={p.subscriber?.sex === 'M'} />
                  <Chk label="F" checked={p.subscriber?.sex === 'F'} />
                </div>
              </F>
            </C>
          </div>

          {/* Row: 9b | 10b | 11b */}
          <div className="grid grid-cols-3" style={{ borderColor: B }}>
            <C><F n="9b" label="RESERVED FOR NUCC USE"><V>{'\u00A0'}</V></F></C>
            <C>
              <F n="" label="b. AUTO ACCIDENT? &nbsp;&nbsp;&nbsp; PLACE (State)">
                <div className="flex gap-1"><Chk label="YES" /><Chk label="NO" checked /></div>
              </F>
            </C>
            <C className="!border-r-0"><F n="11b" label="OTHER CLAIM ID (Designated by NUCC)"><V>{'\u00A0'}</V></F></C>
          </div>

          {/* Row: 9c | 10c | 11c */}
          <div className="grid grid-cols-3" style={{ borderColor: B }}>
            <C><F n="9c" label="RESERVED FOR NUCC USE"><V>{'\u00A0'}</V></F></C>
            <C>
              <F n="" label="c. OTHER ACCIDENT?">
                <div className="flex gap-1"><Chk label="YES" /><Chk label="NO" checked /></div>
              </F>
            </C>
            <C className="!border-r-0"><F n="11c" label="INSURANCE PLAN NAME OR PROGRAM NAME"><V>{'\u00A0'}</V></F></C>
          </div>

          {/* Row: 9d | 10d | 11d */}
          <div className="grid grid-cols-3" style={{ borderColor: B }}>
            <C><F n="9d" label="INSURANCE PLAN NAME OR PROGRAM NAME"><V mono>{p.secondary_insurance?.name ? String(p.secondary_insurance.name).toUpperCase() : '\u00A0'}</V></F></C>
            <C><F n="10d" label="CLAIM CODES (Designated by NUCC)"><V>{'\u00A0'}</V></F></C>
            <C className="!border-r-0">
              <F n="11d" label="IS THERE ANOTHER HEALTH BENEFIT PLAN?">
                <div className="flex gap-1" data-testid="box-11d-cob">
                  <Chk label="YES" checked={!!p.secondary_insurance} />
                  <Chk label="NO" checked={!p.secondary_insurance} />
                </div>
              </F>
            </C>
          </div>

          {/* Row: 12 | 13 */}
          <div className="grid grid-cols-2" style={{ borderColor: B }}>
            <C>
              <F n="12" label="PATIENT'S OR AUTHORIZED PERSON'S SIGNATURE  I authorize the release of any medical or other information necessary to process this claim.">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] italic text-gray-600">Signature on File</span>
                  <span style={{ fontSize: '7px', color: '#888' }}>DATE</span>
                </div>
              </F>
            </C>
            <C className="!border-r-0">
              <F n="13" label="INSURED'S OR AUTHORIZED PERSON'S SIGNATURE  I authorize payment of medical benefits to the undersigned physician or supplier.">
                <span className="text-[10px] italic text-gray-600">Signature on File</span>
              </F>
            </C>
          </div>

          {/* ═══ PHYSICIAN OR SUPPLIER INFORMATION ═══ */}
          <div className="px-2 py-[2px] text-center" style={{ background: LR, borderBottom: `1px solid ${B}` }}>
            <span className="uppercase tracking-[0.15em] font-bold" style={{ fontSize: '7px', color: R }}>PHYSICIAN OR SUPPLIER INFORMATION</span>
          </div>

          {/* Row: 14 | 15 | 16 */}
          <div className="grid grid-cols-3" style={{ borderColor: B }}>
            <C><F n="14" label="DATE OF CURRENT ILLNESS, INJURY, or PREGNANCY (LMP)"><V mono>{p.claim?.current_illness_date ? fmtDos(p.claim.current_illness_date) : '\u00A0'}</V></F></C>
            <C><F n="15" label="OTHER DATE"><V>{'\u00A0'}</V></F></C>
            <C className="!border-r-0">
              <F n="16" label="DATES PATIENT UNABLE TO WORK IN CURRENT OCCUPATION">
                <div className="flex gap-2">
                  <span style={{ fontSize: '6px', color: '#888' }}>FROM</span><V>{'\u00A0'}</V>
                  <span style={{ fontSize: '6px', color: '#888' }}>TO</span><V>{'\u00A0'}</V>
                </div>
              </F>
            </C>
          </div>

          {/* Row: 17 | 17a/17b | 18 */}
          <div className="grid grid-cols-3" style={{ borderColor: B }}>
            <C><F n="17" label="NAME OF REFERRING PROVIDER OR OTHER SOURCE"><V>{'\u00A0'}</V></F></C>
            <C>
              <div>
                <div className="px-1 pt-[1px]">
                  <span style={{ fontSize: '6px', color: R }} className="font-semibold">17a. </span>
                  <V>{'\u00A0'}</V>
                </div>
                <div className="px-1 border-t" style={{ borderColor: '#ccc' }}>
                  <span style={{ fontSize: '6px', color: R }} className="font-semibold">17b. NPI </span>
                  <V>{'\u00A0'}</V>
                </div>
              </div>
            </C>
            <C className="!border-r-0">
              <F n="18" label="HOSPITALIZATION DATES RELATED TO CURRENT SERVICES">
                <div className="flex gap-2">
                  <span style={{ fontSize: '6px', color: '#888' }}>FROM</span><V>{'\u00A0'}</V>
                  <span style={{ fontSize: '6px', color: '#888' }}>TO</span><V>{'\u00A0'}</V>
                </div>
              </F>
            </C>
          </div>

          {/* Row: 19 | 20 */}
          <div className="grid grid-cols-2" style={{ borderColor: B }}>
            <C><F n="19" label="ADDITIONAL CLAIM INFORMATION (Designated by NUCC)"><V>{'\u00A0'}</V></F></C>
            <C className="!border-r-0">
              <F n="20" label="OUTSIDE LAB? &nbsp;&nbsp;&nbsp;&nbsp; $ CHARGES">
                <div className="flex gap-2"><Chk label="YES" /><Chk label="NO" checked /><V mono>$ 0.00</V></div>
              </F>
            </C>
          </div>

          {/* ═══ BOX 21 — DIAGNOSIS ═══ */}
          <div className="border-b" style={{ borderColor: B }}>
            <div className="px-1.5 py-[2px] flex items-center justify-between" style={{ background: LR, borderBottom: `1px solid ${B}` }}>
              <span className="uppercase tracking-wide font-bold" style={{ fontSize: '7px', color: R }}>
                21. DIAGNOSIS OR NATURE OF ILLNESS OR INJURY  Relate A-L to service line below (24E)
              </span>
              <span style={{ fontSize: '6.5px', color: '#888' }}>ICD Ind. <span className="font-mono font-bold text-gray-800 text-[9px]">0</span></span>
            </div>
            <div className="grid grid-cols-4">
              {[0,1,2,3,4,5,6,7,8,9,10,11].map(i => {
                const dx = p.diagnosis_codes?.[i];
                const letter = String.fromCharCode(65 + i);
                return (
                  <div key={i} className="px-1 py-[2px] flex items-center gap-1 min-h-[20px] border-r border-b" style={{ borderColor: '#ccc' }}>
                    <span className="font-bold w-[10px]" style={{ fontSize: '7px', color: R }}>{letter}.</span>
                    <span className="text-[10px] font-mono text-gray-900">{dx || ''}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Row: 22 | 23 */}
          <div className="grid grid-cols-2" style={{ borderColor: B }}>
            <C>
              <F n="22" label="RESUBMISSION CODE &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ORIGINAL REF. NO.">
                <V>{'\u00A0'}</V>
              </F>
            </C>
            <C className="!border-r-0">
              <F n="23" label="PRIOR AUTHORIZATION NUMBER"><V mono>{p.claim?.authorization_number}</V></F>
            </C>
          </div>

          {/* ═══ BOX 24 — SERVICE LINES ═══ */}
          {(!p.service_lines || p.service_lines.length === 0) && (
            <div className="px-3 py-2 flex items-center gap-2 text-amber-800 border-b" style={{ background: '#fef3c7', borderColor: B }} data-testid="no-service-lines-warning">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <span className="text-[11px] font-semibold">No service lines — the source note has no CPT code assigned. Set a CPT code on the note or linked appointment before billing.</span>
            </div>
          )}
          <div className="border-b" style={{ borderColor: B }}>
            <table className="w-full text-[10px] border-collapse" style={{ borderColor: B }}>
              <thead>
                <tr style={{ background: LR }}>
                  {[
                    { w: '20px', text: '' },
                    { w: '120px', text: <span><span style={{color:R,fontWeight:700}}>A.</span> DATE(S) OF SERVICE<br/><span style={{ fontSize: '5.5px', color: '#888' }}>MM DD YY &nbsp;&nbsp; MM DD YY</span><br/><span style={{ fontSize: '5.5px', color: '#888' }}>From &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; To</span></span> },
                    { w: '32px', text: <span><span style={{color:R,fontWeight:700}}>B.</span><br/>PLACE OF<br/>SERVICE</span> },
                    { w: '24px', text: <span><span style={{color:R,fontWeight:700}}>C.</span><br/>EMG</span> },
                    { w: 'auto', text: <span><span style={{color:R,fontWeight:700}}>D.</span> PROCEDURES, SERVICES, OR SUPPLIES<br/><span style={{ fontSize: '5.5px', color: '#888' }}>CPT/HCPCS &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; MODIFIER</span></span> },
                    { w: '32px', text: <span><span style={{color:R,fontWeight:700}}>E.</span><br/>DIAGNOSIS<br/>POINTER</span> },
                    { w: '65px', text: <span><span style={{color:R,fontWeight:700}}>F.</span><br/>$ CHARGES</span> },
                    { w: '42px', text: <span><span style={{color:R,fontWeight:700}}>G.</span><br/>DAYS OR<br/>UNITS</span> },
                    { w: '32px', text: <span><span style={{color:R,fontWeight:700}}>H.</span><br/>EPSDT<br/>Family Plan</span> },
                    { w: '36px', text: <span><span style={{color:R,fontWeight:700}}>I.</span><br/>ID.<br/>QUAL.</span> },
                    { w: '90px', text: <span><span style={{color:R,fontWeight:700}}>J.</span><br/>RENDERING<br/>PROVIDER ID. #</span> },
                  ].map((col, ci) => (
                    <th key={ci} className="border px-0.5 py-[2px] text-center" style={{ borderColor: B, fontSize: '6px', color: '#555', fontWeight: 600, width: col.w }}>
                      {col.text}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[0,1,2,3,4,5].map(i => {
                  const sl = p.service_lines?.[i];
                  return (
                    <tr key={i} className={sl ? 'bg-white' : 'bg-gray-50'} style={{ opacity: sl ? 1 : 0.35 }}>
                      <td className="border px-0.5 py-[3px] text-center text-gray-400" style={{ borderColor: '#bbb', fontSize: '7px' }}>{i + 1}</td>
                      <td className="border px-1 py-[3px] font-mono text-[10px]" style={{ borderColor: '#bbb' }}>
                        {sl ? `${fmtDos(sl.date_from)}${sl.date_to && sl.date_to !== sl.date_from ? '  ' + fmtDos(sl.date_to) : ''}` : ''}
                      </td>
                      <td className="border px-0.5 py-[3px] font-mono text-center text-[10px]" style={{ borderColor: '#bbb' }}>{sl?.pos || ''}</td>
                      <td className="border px-0.5 py-[3px] text-center" style={{ borderColor: '#bbb' }}>{''}</td>
                      <td className="border px-1 py-[3px] font-mono text-[10px]" style={{ borderColor: '#bbb' }}>
                        {sl ? <><span className="font-bold text-gray-900">{sl.cpt_code}</span>{sl.modifiers?.length ? <span className="ml-3 text-gray-700">{sl.modifiers.join('  ')}</span> : null}</> : ''}
                      </td>
                      <td className="border px-0.5 py-[3px] text-center text-[10px]" style={{ borderColor: '#bbb' }}>
                        {sl?.diagnosis_pointers?.map(d => String.fromCharCode(64 + d)).join(',') || ''}
                      </td>
                      <td className="border px-1 py-[3px] font-mono text-[10px]" style={{ borderColor: '#bbb' }}
                          title={sl && (sl.units || 1) > 1
                            ? `Per-unit: $${Number(sl.charges || 0).toFixed(2)} × ${sl.units} units = line total below`
                            : undefined}>
                        {/* CMS-1500 Box 24F spec: dollar amount is the
                            LINE TOTAL (per-unit × units), mirroring
                            SV102 on the 837P wire.  ``sl.charges`` is
                            stored as per-unit, so we multiply on
                            display.  Bug fix May 2026 — biller saw
                            per-unit here and resubmitted with the
                            wrong total, claim denied for amount
                            mismatch. */}
                        {sl ? (Number(sl.charges || 0) * Number(sl.units || 1)).toFixed(2) : ''}
                      </td>
                      <td className="border px-0.5 py-[3px] text-center font-mono text-[10px]" style={{ borderColor: '#bbb' }}>{sl?.units || ''}</td>
                      <td className="border px-0.5 py-[3px] text-center" style={{ borderColor: '#bbb' }}>{''}</td>
                      <td className="border px-0.5 py-[3px] text-center" style={{ borderColor: '#bbb', fontSize: '7px' }}>{sl ? 'NPI' : ''}</td>
                      <td
                        className="border px-1 py-[3px] font-mono text-[10px] cursor-help"
                        style={{ borderColor: '#bbb' }}
                        title={sl && p.rendering_provider?.npi
                          ? `Rendering provider — Loop 2310B NM1*82\n${p.rendering_provider?.name || ''}${p.rendering_provider?.credentials ? ', ' + p.rendering_provider.credentials : ''}\nNPI: ${p.rendering_provider.npi}`
                          : ''}
                        data-testid={sl ? `box-24j-rendering-npi-line-${i + 1}` : undefined}
                      >
                        {sl ? (p.rendering_provider?.npi || '') : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ═══ BOXES 25-30 ═══ */}
          <div className="grid grid-cols-6 border-b" style={{ borderColor: B }}>
            <C>
              <F n="25" label="FEDERAL TAX I.D. NUMBER">
                <div className="flex items-center gap-1"><V mono>{p.submitter?.tax_id}</V><Chk label="SSN" /><Chk label="EIN" checked /></div>
              </F>
            </C>
            <C><F n="26" label="PATIENT'S ACCOUNT NO."><V mono>{p.subscriber?.mrn}</V></F></C>
            <C><F n="27" label="ACCEPT ASSIGNMENT?"><div className="flex gap-1"><Chk label="YES" checked /><Chk label="NO" /></div></F></C>
            <C><F n="28" label="TOTAL CHARGE">
              <div className="flex items-baseline gap-1 pl-1">
                <span className="text-[9px] text-gray-500 font-mono">$</span>
                <V mono>{Number(p.claim?.total_charges || 0).toFixed(2)}</V>
              </div>
            </F></C>
            <C><F n="29" label="AMOUNT PAID">
              <div className="flex items-baseline gap-1 pl-1">
                <span className="text-[9px] text-gray-500 font-mono">$</span>
                <V mono>0.00</V>
              </div>
            </F></C>
            <C className="!border-r-0"><F n="30" label="Rsvd for NUCC Use"><V>{'\u00A0'}</V></F></C>
          </div>

          {/* ═══ BOXES 31, 32, 33 ═══ */}
          <div className="grid grid-cols-3">
            <div className="border-r border-b" style={{ borderColor: B }}>
              <F n="31" label="SIGNATURE OF PHYSICIAN OR SUPPLIER INCLUDING DEGREES OR CREDENTIALS (I certify that the statements on the reverse apply to this bill and are made a part thereof.)">
                <div className="flex items-end justify-between min-h-[38px]">
                  <span className="text-[10px] italic text-gray-600" data-testid="box-31-signature">
                    {(() => {
                      // Resolution order matches backend mapper:
                      //   1. Per-payer override (insurance.box31_signature)
                      //   2. Auto: facility name when billing as facility,
                      //      otherwise rendering provider name + creds.
                      const override = (p.insurance?.box31_signature || '').toLowerCase();
                      if (override === 'facility') {
                        return p.billing_provider?.name || 'Signature on File';
                      }
                      if (override === 'rendering_provider') {
                        return (
                          <>
                            {p.rendering_provider?.name || 'Signature on File'}
                            {p.rendering_provider?.credentials ? `, ${p.rendering_provider.credentials}` : ''}
                          </>
                        );
                      }
                      if (p.billing_provider?.entity_type === 'facility') {
                        return p.billing_provider?.name || 'Signature on File';
                      }
                      return (
                        <>
                          {p.rendering_provider?.name || 'Signature on File'}
                          {p.rendering_provider?.credentials ? `, ${p.rendering_provider.credentials}` : ''}
                        </>
                      );
                    })()}
                  </span>
                  <span style={{ fontSize: '7px', color: '#888' }}>DATE {fmtDos(p.claim?.date_of_service)}</span>
                </div>
              </F>
            </div>
            <div className="border-r border-b" style={{ borderColor: B }}>
              <F n="32" label="SERVICE FACILITY LOCATION INFORMATION">
                {/* Jun 2026 — prefer the resolved service facility (parsed.facility:
                    note location → appointment → provider's office). Falls back to
                    submitter (billing/primary address) for older payloads. */}
                {(() => {
                  const fac = p.facility || p.submitter || {};
                  return (
                    <div className="space-y-[1px] min-h-[38px]" data-testid="box-32-facility">
                      <div className="text-[10px] text-gray-900">{fac.name}</div>
                      <div className="text-[9px] text-gray-700">{fac.address}</div>
                      <div className="text-[9px] text-gray-700">{[fac.city, fac.state].filter(Boolean).join(', ')} {fac.zip}</div>
                    </div>
                  );
                })()}
              </F>
              <div className="grid grid-cols-2 border-t" style={{ borderColor: '#ccc' }}>
                <div
                  className="px-1 py-[1px] border-r cursor-help"
                  style={{ borderColor: '#ccc' }}
                  title={(p.facility?.npi || p.submitter?.npi || p.billing_provider?.npi)
                    ? `Service facility — Loop 2310C NM1*77\n${p.facility?.name || p.submitter?.name || p.billing_provider?.name || ''}\nNPI: ${p.facility?.npi || p.submitter?.npi || p.billing_provider?.npi || ''}`
                    : ''}
                >
                  <span style={{ fontSize: '6px', color: R }} className="font-semibold">a. NPI </span>
                  <span className="text-[9px] font-mono text-gray-900" data-testid="box-32a-npi">
                    {/* Service Facility NPI: the resolved facility's own NPI wins;
                        then org/submitter NPI; then billing provider NPI so
                        payer-specific overrides surface when the others are unset. */}
                    {p.facility?.npi || p.submitter?.npi || p.billing_provider?.npi || ''}
                  </span>
                </div>
                <div className="px-1 py-[1px] cursor-help"
                  title={p.facility?.other_id
                    ? `Service facility secondary ID — mirrors the REF segment on Loop 2310C/2310D\n${p.facility?.other_id}`
                    : ''}
                >
                  <span style={{ fontSize: '6px', color: R }} className="font-semibold">b. </span>
                  <span className="text-[9px] font-mono text-gray-900" data-testid="box-32b-other-id">
                    {/* Box 32b mirror — the location's EDI secondary ID
                        ("<qualifier> <value>"), exactly what payers receive
                        in the service facility REF segment. */}
                    {p.facility?.other_id || ''}
                  </span>
                </div>
              </div>
            </div>
            <div className="border-b" style={{ borderColor: B }}>
              <F n="33" label="BILLING PROVIDER INFO &amp; PH #">
                <div className="space-y-[1px] min-h-[38px]">
                  <div className="text-[10px] text-gray-900">
                    {p.billing_provider?.name}{p.billing_provider?.credentials ? `, ${p.billing_provider.credentials}` : ''}
                  </div>
                  <div className="text-[9px] text-gray-700">{p.submitter?.phone}</div>
                </div>
              </F>
              <div className="grid grid-cols-2 border-t" style={{ borderColor: '#ccc' }}>
                <div className="px-1 py-[1px] border-r" style={{ borderColor: '#ccc' }}>
                  <span style={{ fontSize: '6px', color: R }} className="font-semibold">a. NPI </span>
                  <span className="text-[9px] font-mono text-gray-900">{p.billing_provider?.npi}</span>
                </div>
                <div className="px-1 py-[1px]">
                  <span style={{ fontSize: '6px', color: R }} className="font-semibold">b. </span>
                  <span className="text-[9px] font-mono text-gray-700">{p.billing_provider?.billing_credential || ''}</span>
                  {p.billing_provider?.taxonomy && <span className="text-[8px] font-mono text-gray-400 ml-1">{p.billing_provider.taxonomy}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="flex items-center justify-between px-2 py-[3px]" style={{ background: LR, borderTop: `1px solid ${R}` }}>
            <span className="uppercase tracking-wider" style={{ fontSize: '6.5px', color: '#888' }}>NUCC Instruction Manual available at: www.nucc.org</span>
            <span className="uppercase tracking-wider font-bold" style={{ fontSize: '6.5px', color: '#888' }}>PLEASE PRINT OR TYPE</span>
            <span className="uppercase tracking-wider" style={{ fontSize: '6.5px', color: '#888' }}>APPROVED OMB-0938-1197 FORM 1500 (02-12)</span>
          </div>
        </div>
      </div>
    </div>
  
  );
};

export default ClaimViewer;

// ─────────────────────────────────────────────────────────────────────
// LoopsView
// ─────────────────────────────────────────────────────────────────────
// Renders the EDI 837P grouped by X12 loop with per-payer override
// badges.  Each loop is a card showing its loop_id + human-readable
// name, its segments, and any override metadata returned by the
// backend (`services/edi_loop_grouper.group_837p_by_loop`).
//
// Loops with overrides are highlighted with an amber left border so
// billers can spot at a glance which sections of the EDI were
// influenced by per-payer config (BCBS 2310B override, Medicare
// incident-to swap, etc.).  Hovering an override badge surfaces the
// exact source (e.g. "Insurance Co → loop_2310b_override").
const _BADGE_COLOR_MAP = {
  amber: 'bg-amber-100 text-amber-800 border-amber-300',
  blue: 'bg-blue-100 text-blue-800 border-blue-300',
  purple: 'bg-purple-100 text-purple-800 border-purple-300',
  red: 'bg-red-100 text-red-800 border-red-300',
  gray: 'bg-gray-100 text-gray-700 border-gray-300',
};

const LoopsView = ({ loops }) => {
  if (!loops || loops.length === 0) {
    return (
      <div className="p-6 text-sm text-gray-500">
        No EDI loop data available. Generate the 837P first.
      </div>
    );
  }
  const overrideCount = loops.reduce(
    (n, g) => n + g.segments.filter((s) => (s.overrides || []).length > 0).length,
    0,
  );
  return (
    <div className="p-5 space-y-3" data-testid="edi-loops-view">
      <div className="flex items-center justify-between text-xs text-gray-600 pb-2 border-b">
        <div>
          <span className="font-medium">{loops.length}</span> loops ·{' '}
          <span className="font-medium">
            {loops.reduce((n, g) => n + g.segments.length, 0)}
          </span>{' '}
          segments
        </div>
        {overrideCount > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
            <span>
              <span className="font-medium text-amber-700">{overrideCount}</span>{' '}
              segment{overrideCount === 1 ? '' : 's'} influenced by per-payer config
            </span>
          </div>
        )}
      </div>
      {loops.map((g, idx) => (
        <div
          key={`${g.loop_id}-${idx}`}
          className={`border rounded-md ${g.has_overrides ? 'border-l-4 border-l-amber-500 bg-amber-50/30' : 'bg-white'}`}
          data-testid={`edi-loop-${g.loop_id}`}
        >
          <div className="px-3 py-2 border-b bg-gray-50/80 flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-xs font-semibold text-indigo-700">
                {g.loop_id}
              </span>
              <span className="text-sm text-gray-800">{g.loop_name}</span>
            </div>
            <span className="text-[10px] text-gray-500">
              {g.segments.length} segment{g.segments.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="p-3 space-y-1.5">
            {g.segments.map((s, si) => (
              <div key={si} className="text-xs">
                <div className="flex items-start gap-2">
                  <code className="flex-1 font-mono text-gray-800 break-all">
                    {s.raw}
                  </code>
                  {(s.overrides || []).map((o, oi) => (
                    <span
                      key={oi}
                      title={o.source}
                      className={`text-[10px] px-1.5 py-0.5 rounded border whitespace-nowrap ${_BADGE_COLOR_MAP[o.color] || _BADGE_COLOR_MAP.gray}`}
                    >
                      {o.label}
                    </span>
                  ))}
                </div>
                {s.description && (
                  <div className="text-[10px] text-gray-500 pl-1 mt-0.5">
                    {s.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────
// SubmittedAnsiTab
// ─────────────────────────────────────────────────────────────────────
// Renders the exact ST→SE ANSI block that was stamped onto this claim
// the moment its parent batch was closed/submitted.  Feb 2026 Phase A
// of the "preserved ANSI at submission" feature.
//
// Three states:
//   1. Loading — spinner.
//   2. No snapshot — claim hasn't been batched yet, OR predates the
//      snapshot feature (i.e., was submitted before this rolled out).
//      We show a friendly explanation so the biller doesn't think
//      something is broken.
//   3. Snapshot present — metadata header (batch, status, transport,
//      ISA control, submitted_at) + the raw EDI block.  An optional
//      "Compare with current preview" toggle stacks the current
//      preview alongside so the biller can spot drift.  Full
//      character-by-character diff lands in Phase B.
const _SnapshotStatusBadge = ({ status }) => {
  const color =
    status === 'submitted'
      ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
      : status === 'closed_pending_submission'
        ? 'bg-amber-100 text-amber-800 border-amber-300'
        : 'bg-gray-100 text-gray-700 border-gray-300';
  const label =
    status === 'submitted'
      ? 'Submitted'
      : status === 'closed_pending_submission'
        ? 'Closed (pending submission)'
        : status || 'Unknown';
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded border ${color}`}>
      {label}
    </span>
  );
};

const SubmittedAnsiTab = ({
  loading,
  error,
  snapshotData,
  currentPreview,
  showCompare,
  onToggleCompare,
  onRefresh,
}) => {
  // Jun 2026 — every submitted version (current snapshot + preserved
  // history from resubmits / secondary submissions) is selectable so
  // the biller can see exactly what was wired out, and when.
  const versions = React.useMemo(() => {
    const list = [];
    if (snapshotData?.current_snapshot) list.push({ ...snapshotData.current_snapshot, _isLatest: true });
    (snapshotData?.history || []).forEach((s) => { if (s) list.push(s); });
    list.sort(
      (a, b) =>
        new Date(b.submitted_at || b.snapshot_at || 0) -
        new Date(a.submitted_at || a.snapshot_at || 0),
    );
    return list;
  }, [snapshotData]);
  const [selectedIdx, setSelectedIdx] = React.useState(0);
  React.useEffect(() => { setSelectedIdx(0); }, [snapshotData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Loading submitted ANSI snapshot...
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <AlertCircle className="h-8 w-8 text-red-400 mb-3" />
        <p className="text-red-600 font-medium mb-3">{error}</p>
        <Button size="sm" variant="outline" onClick={onRefresh} data-testid="submitted-ansi-retry-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1" />
          Retry
        </Button>
      </div>
    );
  }
  const snap = versions[selectedIdx] || versions[0];
  if (!snap) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center text-gray-500 max-w-lg mx-auto">
        <FileText className="h-10 w-10 text-gray-300 mb-3" />
        <p className="text-sm font-medium text-gray-700 mb-1">
          No submitted ANSI snapshot for this claim
        </p>
        <p className="text-xs text-gray-500">
          The exact ANSI is stamped on the claim the moment its batch is
          closed/submitted. This claim either hasn&apos;t been batched yet,
          or was submitted before this feature rolled out. Future
          submissions will appear here automatically.
        </p>
      </div>
    );
  }
  const submittedTs = snap.submitted_at || snap.snapshot_at;
  return (
    <div className="p-5 space-y-3" data-testid="submitted-ansi-view">
      {/* Submission version selector — one entry per transmitted file */}
      {versions.length > 1 && (
        <div className="border rounded-md bg-white p-2.5" data-testid="submitted-ansi-version-list">
          <div className="text-[11px] font-medium text-gray-500 mb-1.5">
            {versions.length} submissions recorded for this claim — select one to view
            the exact ANSI transmitted
          </div>
          <div className="flex flex-wrap gap-1.5">
            {versions.map((v, i) => {
              const ts = v.submitted_at || v.snapshot_at;
              const active = i === selectedIdx;
              return (
                <button
                  key={`${v.batch_id || 'b'}-${ts || i}`}
                  type="button"
                  onClick={() => setSelectedIdx(i)}
                  className={`px-2.5 py-1 rounded-md border text-[11px] font-mono transition-colors ${
                    active
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                      : 'border-gray-200 text-gray-600 hover:border-emerald-300 hover:bg-emerald-50/50'
                  }`}
                  data-testid={`submitted-ansi-version-item-${i}`}
                >
                  {ts ? new Date(ts).toLocaleString() : 'Unknown time'}
                  {v.batch_number != null && ` · Batch #${v.batch_number}`}
                  {v.claim_version != null && ` · v${v.claim_version}`}
                  {v.status === 'submitted' ? ' · sent' : ' · closed'}
                  {v._isLatest && ' · latest'}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* Metadata header */}
      <div className="border rounded-md bg-gray-50 p-3 text-xs">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-gray-700">
          <div className="flex items-center gap-1.5">
            <span className="font-medium">Status:</span>
            <_SnapshotStatusBadge status={snap.status} />
          </div>
          {snap.batch_number != null && (
            <div>
              <span className="font-medium">Batch:</span>{' '}
              <span className="font-mono">#{snap.batch_number}</span>
            </div>
          )}
          {snap.clearinghouse && (
            <div>
              <span className="font-medium">Clearinghouse:</span>{' '}
              <span className="font-mono uppercase">{snap.clearinghouse}</span>
            </div>
          )}
          {snap.submission_transport && (
            <div>
              <span className="font-medium">Transport:</span>{' '}
              <span className="font-mono">{snap.submission_transport}</span>
            </div>
          )}
          {snap.isa_control_number && (
            <div>
              <span className="font-medium">ISA Ctrl:</span>{' '}
              <span className="font-mono">{snap.isa_control_number}</span>
            </div>
          )}
          {snap.transaction_number != null && (
            <div>
              <span className="font-medium">ST Txn:</span>{' '}
              <span className="font-mono">
                {String(snap.transaction_number).padStart(4, '0')}
              </span>
            </div>
          )}
          {snap.sftp_filename && (
            <div>
              <span className="font-medium">File:</span>{' '}
              <span className="font-mono">{snap.sftp_filename}</span>
            </div>
          )}
          {submittedTs && (
            <div>
              <span className="font-medium">
                {snap.status === 'submitted' ? 'Submitted at:' : 'Snapshot at:'}
              </span>{' '}
              <span className="font-mono">{new Date(submittedTs).toLocaleString()}</span>
            </div>
          )}
          {snap.submitted_outside_system && (
            <span className="text-[10px] px-2 py-0.5 rounded border bg-indigo-100 text-indigo-800 border-indigo-300">
              Marked submitted outside system
            </span>
          )}
        </div>
        {snapshotData?.history?.length > 0 && versions.length <= 1 && (
          <div className="mt-2 pt-2 border-t border-gray-200 text-[11px] text-gray-500">
            {snapshotData.history.length} prior snapshot
            {snapshotData.history.length === 1 ? '' : 's'} from earlier batch
            close{snapshotData.history.length === 1 ? '' : 's'} /
            resubmission{snapshotData.history.length === 1 ? '' : 's'} (preserved
            for audit).
          </div>
        )}
      </div>

      {/* Compare toggle */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Side-by-side compare highlights drift between what was wired and the
          current preview.
        </div>
        <Button
          size="sm"
          variant={showCompare ? 'default' : 'outline'}
          onClick={onToggleCompare}
          disabled={!currentPreview}
          data-testid="submitted-ansi-compare-btn"
        >
          <Code className="h-3.5 w-3.5 mr-1" />
          {showCompare ? 'Hide compare' : 'Compare with current preview'}
        </Button>
      </div>

      {/* EDI block(s) */}
      {showCompare && currentPreview ? (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] font-medium text-gray-600 mb-1">
              Submitted (wired at batch close)
            </div>
            <div className="bg-gray-950 min-h-[400px] p-4 rounded-md">
              <pre
                className="text-green-400 text-[11px] font-mono whitespace-pre-wrap break-all leading-relaxed"
                data-testid="submitted-ansi-content"
              >
                {snap.edi_content || 'No EDI content recorded.'}
              </pre>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-medium text-gray-600 mb-1">
              Current preview (live, re-rendered from DB)
            </div>
            <div className="bg-gray-950 min-h-[400px] p-4 rounded-md">
              <pre
                className="text-amber-300 text-[11px] font-mono whitespace-pre-wrap break-all leading-relaxed"
                data-testid="current-preview-ansi-content"
              >
                {currentPreview}
              </pre>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-950 min-h-[400px] p-5 rounded-md">
          <pre
            className="text-green-400 text-xs font-mono whitespace-pre-wrap break-all leading-relaxed"
            data-testid="submitted-ansi-content"
          >
            {snap.edi_content || 'No EDI content recorded.'}
          </pre>
        </div>
      )}
    </div>
  );
};

